import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, Building, Package, Users, Edit2, X, Check, Trash2, Calendar, Download, Clock, Search } from 'lucide-react';
import axios from 'axios';
import { RjActivityFilter, useRjActivity } from '@/components/RjActivityFilter';
import { useEmployeeActivityRefresh } from '@/utils/employeeActivity';
import * as XLSX from 'xlsx';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const AdminStatsColisPrive = ({ user, activeModule = "b2b" }) => {
  const [allEmployeesForActivity, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [allSchedulesForActivity, setSchedules] = useState([]);
  const [pdas, setPdas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week');
  const [rjDisplayMode, setRjDisplayMode] = useState('simple');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('all');
  const [quickFilter, setQuickFilter] = useState('all');
  const [expandedWarnings, setExpandedWarnings] = useState({});
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [quickFilterDropdownOpen, setQuickFilterDropdownOpen] = useState(false);
  const employeeDropdownRef = useRef(null);
  const quickFilterDropdownRef = useRef(null);
  const [activeMainTab, setActiveMainTab] = useState('rj');
  const [allUnclosedForActivity, setUnclosedMissions] = useState([]);
  const [loadingUnclosed, setLoadingUnclosed] = useState(false);
  const [closingMissionUserId, setClosingMissionUserId] = useState(null);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 640 : false));
  const pendingValidationLabel = isMobileViewport ? 'À valider' : 'En attente de validation';
  
  // Hours management state
  const [hoursDate, setHoursDate] = useState(new Date());
  const [allHoursForActivity, setHoursSchedules] = useState([]);
  const [editedHours, setEditedHours] = useState({});
  const [savingHours, setSavingHours] = useState(false);
  const { employees, schedules, hoursSchedules, unclosedMissions, activity, setActivity } = useRjActivity(allEmployeesForActivity, allSchedulesForActivity, allHoursForActivity, allUnclosedForActivity);
  
  // Edit modal state
  const [editModal, setEditModal] = useState({
    open: false,
    schedule: null,
    delivered: 0,
    returned: 0,
    picked: 0,
    vehiclePlate: '',
    pdaId: '',
    saving: false,
    deleting: false,
    isNew: false,
    fromUnclosedClose: false
  });

  const isColisPriveSiteName = (siteName = '') => {
    const normalized = String(siteName || '').toLowerCase();
    return normalized.includes('colis') || normalized.includes('privé') || normalized.includes('prive');
  };

  const getDateRange = () => {
    if (viewMode === 'custom') {
      if (!customRange.start || !customRange.end) {
        return null;
      }
      const orderedStart = customRange.start <= customRange.end ? customRange.start : customRange.end;
      const orderedEnd = customRange.start <= customRange.end ? customRange.end : customRange.start;
      return { start: orderedStart, end: orderedEnd };
    }

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    if (viewMode === 'day') {
      // Format date without timezone issues
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      return { start: dateStr, end: dateStr };
    } else if (viewMode === 'week') {
      const day = currentDate.getDay();
      const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1);
      const weekStart = new Date(year, month, diff);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const startStr = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
      const endStr = `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, '0')}-${String(weekEnd.getDate()).padStart(2, '0')}`;
      return { start: startStr, end: endStr };
    } else {
      // Month view - get first and last day of month
      const monthEnd = new Date(year, month + 1, 0); // Last day of month
      const startStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
      const endStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(monthEnd.getDate()).padStart(2, '0')}`;
      return { start: startStr, end: endStr };
    }
  };

  // Format today's date without timezone issues
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const isFutureDate = (dateStr) => (dateStr || '') > today;
  const formatValidatedAtParis = (isoDate) => {
    if (!isoDate) return null;
    const parsed = new Date(isoDate);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(parsed);
  };
  const getValidationStatus = (schedule) => String(schedule?.validation_status || '').trim().toLowerCase();
  const isValidationApproved = (schedule) => {
    const validation = getValidationStatus(schedule);
    return validation === 'approved' || validation === 'validated';
  };
  const isOperationalSchedule = (schedule) => {
    if (!schedule) return false;
    if ((schedule.status || '').toLowerCase() !== 'present') return false;
    if (!isValidationApproved(schedule)) return false;
    if (isFutureDate(schedule.date)) return false;
    return true;
  };

  useEffect(() => {
    fetchData();
    fetchPdas();
    fetchUnclosedMissions();
  }, [currentDate, viewMode, customRange.start, customRange.end]);

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchPdas = async () => {
    try {
      const res = await axios.get(`${API}/pdas?active_only=true`, { withCredentials: true });
      setPdas(res.data.pdas || []);
    } catch (error) {
      console.error('Failed to fetch PDAs:', error);
    }
  };

  const fetchUnclosedMissions = async () => {
    setLoadingUnclosed(true);
    try {
      const res = await axios.get(`${API}/missions/unclosed`, { withCredentials: true });
      const all = res.data?.missions || [];
      const cpOnly = all.filter((mission) => isColisPriveSiteName(mission.site_name));
      setUnclosedMissions(cpOnly);
    } catch (error) {
      console.error('Failed to fetch unclosed CP missions:', error);
      setUnclosedMissions([]);
    } finally {
      setLoadingUnclosed(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const range = getDateRange();
      if (!range) {
        setSchedules([]);
        setLoading(false);
        return;
      }
      const { start, end } = range;
      
      // Fetch data: employees, sites, schedules
      // NOTE: photo_sessions removed - schedule is now the single source of truth
      // (photos are now submitted via WhatsApp, not the platform)
      const [empRes, sitesRes, schedRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/sites`, { withCredentials: true }),
        axios.get(`${API}/schedules?start_date=${start}&end_date=${end}`, { withCredentials: true })
      ]);
      
      const allSites = sitesRes.data || [];
      // Colis Privé sites
      const colisSites = allSites.filter(s => 
        s.name?.toLowerCase().includes('colis') || 
        s.name?.toLowerCase().includes('privé') ||
        s.name?.toLowerCase().includes('prive')
      );
      setSites(colisSites);
      
      // Include ALL employees (active and inactive) for historical RJ data
      // Deactivated employees must still appear in RJ if they performed prestations
      const allEmployees = (empRes.data || []).filter(e => 
        e.role === 'employee'
      );
      setEmployees(allEmployees);
      
      // Filter schedules for Colis Privé sites (schedule is single source of truth)
      const allSchedules = schedRes.data || [];
      const colisSchedules = allSchedules.filter(s => {
        // First check by site_name (most reliable)
        const siteName = (s.site_name || '').toLowerCase();
        if (siteName) {
          // Include only Colis Privé sites
          return siteName.includes('colis') || siteName.includes('privé') || siteName.includes('prive');
        }
        
        // Fallback: check by comment (old method for backward compatibility)
        const comment = s.comment || '';
        return comment.toLowerCase().includes('colis priv') || 
               (comment.includes('Livrés:') || comment.includes('Retours:') || comment.includes('Collectés:') || comment.includes('Ramassés:'));
      });
      
      // Use colisSchedules directly (no more photo_sessions merge)
      setSchedules(colisSchedules);
      console.log('Colis Privé schedules found:', colisSchedules.length);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') newDate.setDate(newDate.getDate() + direction);
    else if (viewMode === 'week') newDate.setDate(newDate.getDate() + (direction * 7));
    else newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getDatesInRange = () => {
    const range = getDateRange();
    if (!range) return [];
    const { start, end } = range;
    const dates = [];
    
    // Parse dates manually to avoid timezone issues
    const [startYear, startMonth, startDay] = start.split('-').map(Number);
    const [endYear, endMonth, endDay] = end.split('-').map(Number);
    
    let current = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);
    
    while (current <= endDate) {
      const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
      dates.push(dateStr);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const getDeliveryStats = (schedule) => {
    // First check if schedule has direct fields
    if (schedule?.delivered !== undefined || schedule?.returned !== undefined || schedule?.picked !== undefined || schedule?.non_collected !== undefined) {
      return {
        delivered: schedule.delivered || 0,
        returned: schedule.returned || 0,
        picked: schedule.picked || 0,
        nonCollected: schedule.non_collected || 0
      };
    }
    
    // Fallback: parse from comment
    const comment = schedule?.comment || '';
    const stats = { delivered: 0, returned: 0, picked: 0, nonCollected: 0 };
    
    const deliveredMatch = comment.match(/Livrés:\s*(\d+)/);
    const returnedMatch = comment.match(/Retours:\s*(\d+)/);
    const pickedMatch = comment.match(/(?:Collectés|Ramassés):\s*(\d+)/);
    const nonCollectedMatch = comment.match(/Non collectés:\s*(\d+)/);
    
    if (deliveredMatch) stats.delivered = parseInt(deliveredMatch[1]) || 0;
    if (returnedMatch) stats.returned = parseInt(returnedMatch[1]) || 0;
    if (pickedMatch) stats.picked = parseInt(pickedMatch[1]) || 0;
    if (nonCollectedMatch) stats.nonCollected = parseInt(nonCollectedMatch[1]) || 0;
    
    return stats;
  };

  // Get employee info by user_id
  const getEmployeeInfo = (userId) => {
    return employees.find(e => e.user_id === userId);
  };

  // Format employee name: "Nom Prénom" format
  const formatEmployeeName = (emp) => {
    if (!emp) return 'Inconnu';
    if (emp.last_name && emp.first_name) {
      return `${emp.last_name} ${emp.first_name}`;
    }
    return emp.name || 'Inconnu';
  };

  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const match = timeStr.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    if (!match) return null;
    return Number(match[1]) * 60 + Number(match[2]);
  };

  const formatMinutesToHm = (minutes) => {
    if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return '';
    const safe = Math.max(0, minutes);
    const h = Math.floor(safe / 60);
    const m = safe % 60;
    return `${h}h${String(m).padStart(2, '0')}`;
  };

  const getHoursMetrics = (schedule) => {
    const startMins = parseTimeToMinutes(schedule?.start_time || '');
    const endMins = parseTimeToMinutes(schedule?.end_time || '');
    if (startMins === null || endMins === null) {
      return {
        realMinutes: 0,
        pauseAutoMinutes: 0,
        presenceITMinutes: 0,
        billableMinutes: 0
      };
    }

    let realMinutes = endMins - startMins;
    if (realMinutes < 0) realMinutes += 24 * 60;
    const pauseAutoMinutes = realMinutes >= 360 ? 20 : 0;
    const presenceITMinutes = Math.max(0, realMinutes - pauseAutoMinutes);
    const billableMinutes = Math.min(420, presenceITMinutes);

    return {
      realMinutes,
      pauseAutoMinutes,
      presenceITMinutes,
      billableMinutes
    };
  };

  const getDeterministicExportBillableMinutes = (schedule, metrics) => {
    if (!metrics || metrics.billableMinutes !== 420 || metrics.realMinutes <= 420) {
      return metrics?.billableMinutes || 0;
    }

    const maxAllowed = Math.max(0, Math.min(430, metrics.realMinutes) - 420);
    const increments = [3, 5, 8, 10].filter((inc) => inc <= maxAllowed);
    if (increments.length === 0) return 420;

    const seed = `${schedule?.schedule_id || ''}|${schedule?.user_id || ''}|${schedule?.date || ''}|${schedule?.pda_id || ''}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = (hash * 31 + seed.charCodeAt(i)) % 1000003;
    }
    const selectedIncrement = increments[hash % increments.length];
    return 420 + selectedIncrement;
  };

  const getSimpleRjStatus = (schedule, date) => {
    if (!schedule) return { key: 'non_travaille', label: 'Non travaillé' };
    const status = (schedule.status || '').toLowerCase();
    const validation = getValidationStatus(schedule);
    const isFuture = isFutureDate(date || schedule.date);
    const isValidatedMission = isValidationApproved(schedule);

    if (validation === 'rejected' || status === 'annule') return { key: 'annule', label: 'Annulé' };
    // Travaillé UNIQUEMENT si mission validée administrativement
    if (!isFuture && isValidatedMission) return { key: 'travaille', label: 'Travaillé' };
    if (['repos', 'repos_demande', 'repos_force'].includes(status)) return { key: 'repos', label: status === 'repos_force' ? 'Repos forcé' : 'Repos' };
    if (['absent', 'conge', 'cp', 'css', 'am', 'map', 'fdc', 'fpe', 'dem', 'adp'].includes(status)) return { key: 'absence', label: 'Absence' };
    if (status === 'prevu') return { key: 'planifie', label: 'Planifié' };
    if (isFuture && status === 'present') return { key: 'planifie', label: 'Planifié' };
    if (status === 'present') return { key: 'non_valide', label: pendingValidationLabel };
    return { key: 'non_travaille', label: 'Non travaillé' };
  };

  const getSimpleStatusClass = (statusKey) => {
    switch (statusKey) {
      case 'travaille': return 'bg-green-100 text-green-700 border-green-300';
      case 'planifie': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'non_travaille': return 'bg-slate-100 text-slate-700 border-slate-300';
      case 'absence': return 'bg-red-100 text-red-700 border-red-300';
      case 'repos': return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'annule': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'non_valide':
      default: return 'bg-amber-100 text-amber-700 border-amber-300';
    }
  };

  const canShowPrestationBadge = (statusKey) => ['travaille', 'planifie'].includes(statusKey);

  const hasOperationalDataForCp = (dayData) => {
    if (!dayData) return false;
    return ['delivered', 'returned', 'picked', 'nonCollected'].some((key) => Number(dayData[key] || 0) > 0);
  };

  const hasOperationalDataInSchedule = (schedule) => {
    if (!schedule) return false;
    const directFields = ['delivered', 'returned', 'picked', 'non_collected'];
    const hasDirect = directFields.some((field) => schedule[field] !== undefined && schedule[field] !== null);
    if (hasDirect) return true;

    const comment = (schedule.comment || '').toLowerCase();
    return /livr(?:e|é)s?\s*:|retours?\s*:|(?:collect(?:e|é)s?|ramass(?:e|é)s?)\s*:|non\s*collect(?:e|é)s?\s*:/.test(comment);
  };

  const getCpMissionDetails = (schedule, dayData) => {
    if (!schedule) return [];
    const details = [];
    const mission = schedule.tour_type || 'Mission CP';
    details.push(`Type: ${mission}`);
    if (schedule.start_time || schedule.end_time) {
      details.push(`Horaire: ${schedule.start_time || '?'} - ${schedule.end_time || '?'}`);
    }
    if (dayData) {
      details.push(`Livrés: ${dayData.delivered || 0}`);
      details.push(`Retours: ${dayData.returned || 0}`);
      details.push(`Collectes: ${dayData.picked || 0}`);
    }
    if (schedule.employee_comment) {
      details.push(`Commentaire salarié: ${schedule.employee_comment}`);
    }
    if (schedule.comment) {
      details.push(`Commentaire mission: ${schedule.comment}`);
    }
    const lowerTour = (schedule.tour_type || '').toLowerCase();
    if (lowerTour.includes('backup') || lowerTour.includes('assistance')) {
      details.push(`Mission spéciale: ${schedule.tour_type}`);
      if (schedule.backup_packages !== undefined && schedule.backup_packages !== null) {
        details.push(`Colis backup: ${schedule.backup_packages}`);
      }
      if (schedule.backup_returns !== undefined && schedule.backup_returns !== null) {
        details.push(`Retours backup: ${schedule.backup_returns}`);
      }
      if (schedule.assistance_points !== undefined && schedule.assistance_points !== null) {
        details.push(`Points assistance: ${schedule.assistance_points}`);
      }
      if (schedule.assistance_comment) {
        details.push(`Commentaire assistance: ${schedule.assistance_comment}`);
      }
    }
    return details;
  };

  // Group schedules by site
  const getSchedulesBySite = () => {
    const bySite = {};
    
    // Initialize Colis Privé sites
    sites.forEach(site => {
      bySite[site.site_id] = { site_name: site.name, schedules: [], users: new Set() };
    });
    
    // If no Colis Privé sites, create a default one
    if (sites.length === 0) {
      bySite['colis_prive'] = { site_name: 'Colis Prive', schedules: [], users: new Set() };
    }
    
    // IMPORTANT: First, add ALL employees assigned to Colis Privé sites (even without schedules)
    employees.forEach(emp => {
      if (emp.site_id && bySite[emp.site_id]) {
        // Employee is assigned to a Colis Privé site
        bySite[emp.site_id].users.add(emp.user_id);
      }
    });
    
    // Then assign schedules to sites based on employee's site
    schedules.forEach(sched => {
      const emp = getEmployeeInfo(sched.user_id);
      const siteId = emp?.site_id;
      
      // Check if employee's site is a Colis Privé site
      if (siteId && bySite[siteId]) {
        bySite[siteId].schedules.push({
          ...sched,
          user_name: emp?.name || sched.user_name || 'Inconnu'
        });
        bySite[siteId].users.add(sched.user_id);
      } else {
        // Put in the first Colis Privé site or default
        const defaultSiteId = sites.length > 0 ? sites[0].site_id : 'colis_prive';
        if (!bySite[defaultSiteId]) {
          bySite[defaultSiteId] = { site_name: 'Colis Prive', schedules: [], users: new Set() };
        }
        bySite[defaultSiteId].schedules.push({
          ...sched,
          user_name: emp?.name || sched.user_name || 'Inconnu'
        });
        bySite[defaultSiteId].users.add(sched.user_id);
      }
    });
    
    return bySite;
  };

  // Get stats for a user
  // IMPORTANT: Only count approved missions (not rejected)
  const getUserStats = (userId, siteSchedules) => {
    const userSchedules = siteSchedules.filter((s) => s.user_id === userId && isOperationalSchedule(s));
    const totals = { delivered: 0, returned: 0, picked: 0, nonCollected: 0 };
    const byDate = {};
    
    userSchedules.forEach(s => {
      const stats = getDeliveryStats(s);
      totals.delivered += stats.delivered;
      totals.returned += stats.returned;
      totals.picked += stats.picked;
      totals.nonCollected += stats.nonCollected;
      byDate[s.date] = { ...stats, schedule: s, start_time: s.start_time || null, end_time: s.end_time || null };
    });
    
    return { totals, byDate };
  };

  // Export Excel
  const exportToExcel = async (scope = 'all') => {
    const range = getDateRange();
    if (!range) {
      alert('Veuillez sélectionner une période personnalisée complète.');
      return;
    }

    const { start, end } = range;
    const rows = [];

    try {
      const response = await axios.get(`${API}/schedules?start_date=${start}&end_date=${end}&limit=50000`, { withCredentials: true });
      const allSchedules = response.data || [];

      let cpSchedules = allSchedules.filter((s) => {
        const siteName = (s.site_name || '').toLowerCase();
        return siteName.includes('colis') || siteName.includes('privé') || siteName.includes('prive');
      });

      if (scope === 'filtered') {
        cpSchedules = cpSchedules.filter((s) => filteredEmployeeIdSetForExport.has(s.user_id));
      }

      const dedupMap = new Map();
      cpSchedules.forEach((sched) => {
        const key = sched.schedule_id || `${sched.user_id}|${sched.date}|${sched.tour_type || ''}|${sched.start_time || ''}|${sched.end_time || ''}`;
        if (!dedupMap.has(key)) dedupMap.set(key, sched);
      });

      Array.from(dedupMap.values())
        .sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.site_name || '').localeCompare(b.site_name || '') || (a.user_name || '').localeCompare(b.user_name || ''))
        .forEach((daySched) => {
          const stats = getDeliveryStats(daySched);
          const pda = pdas.find(p => p.pda_id === daySched.pda_id);
          const metrics = getHoursMetrics(daySched);
          const exportBillableMinutes = isOperationalSchedule(daySched)
            ? getDeterministicExportBillableMinutes(daySched, metrics)
            : 0;
          const simpleStatus = getSimpleRjStatus(daySched, daySched.date);

          rows.push({
            'Date': daySched.date || '',
            'Site': daySched.site_name || '',
            'Chauffeur': daySched.user_name || formatEmployeeName(getEmployeeInfo(daySched.user_id)),
            'Mission': daySched.tour_type || 'Mission CP',
            'H. Début': daySched.start_time || '',
            'H. Fin': daySched.end_time || '',
            'Statut RJ': simpleStatus.label,
            'Validation': daySched.validation_status || '',
            'Livrés': stats.delivered,
            'Retours': stats.returned,
            'Non Collectes': stats.nonCollected,
            'Collectes': stats.picked,
            'Plaque': daySched.vehicle_plate || '',
            'PDA': pda?.numero || daySched.pda_id || '',
            'Commentaires': daySched.employee_comment || daySched.comment || '',
            'Type spécial': (daySched.tour_type || '').toLowerCase().includes('backup') || (daySched.tour_type || '').toLowerCase().includes('assistance') ? (daySched.tour_type || '') : '',
            'Facturable': formatMinutesToHm(exportBillableMinutes)
          });
        });

      if (rows.length === 0) {
        alert(scope === 'filtered' ? 'Aucune donnée visible à exporter avec les filtres actifs.' : 'Aucune donnée disponible sur cette période');
        return;
      }

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 12 }, { wch: 22 }, { wch: 24 }, { wch: 16 }, { wch: 10 }, { wch: 10 },
        { wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 10 },
        { wch: 14 }, { wch: 12 }, { wch: 32 }, { wch: 18 }, { wch: 12 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'RJ Colis Prive');
      const suffix = scope === 'filtered' ? 'FILTRE' : 'GLOBAL';
      XLSX.writeFile(wb, `RJ_CP_${suffix}_${start}_${end}.xlsx`);
    } catch (error) {
      console.error('Export Colis Privé failed:', error);
      alert('Erreur export: impossible de générer le fichier Excel pour cette période.');
    }
  };

  // Hours management helpers
  const hoursDateStr = useMemo(() => {
    const y = hoursDate.getFullYear();
    const m = String(hoursDate.getMonth() + 1).padStart(2, '0');
    const d = String(hoursDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [hoursDate]);

  // Fetch hours schedules when date changes
  useEffect(() => {
    const fetchHoursData = async () => {
      try {
        const res = await axios.get(`${API}/schedules?start_date=${hoursDateStr}&end_date=${hoursDateStr}`, { withCredentials: true });
        // Filter only Colis Privé schedules
        const cpSiteIds = sites.map(s => s.site_id);
        const filtered = (res.data || []).filter((s) => (cpSiteIds.length === 0 || cpSiteIds.includes(s.site_id)) && !isFutureDate(s.date));
        setHoursSchedules(filtered);
      } catch (error) {
        console.error('Failed to fetch hours data:', error);
      }
    };
    fetchHoursData();
  }, [hoursDateStr, sites, savingHours]);

  const navigateHoursDate = (direction) => {
    const nd = new Date(hoursDate);
    nd.setDate(nd.getDate() + direction);
    setHoursDate(nd);
    setEditedHours({});
  };

  const handleHourChange = (scheduleId, field, value) => {
    setEditedHours(prev => ({
      ...prev,
      [scheduleId]: { ...prev[scheduleId], [field]: value }
    }));
  };

  const saveAllHours = async () => {
    const toSave = Object.entries(editedHours).filter(([_, v]) => v.start_time !== undefined || v.end_time !== undefined);
    if (toSave.length === 0) return;
    
    setSavingHours(true);
    try {
      await Promise.all(toSave.map(([scheduleId, data]) =>
        axios.put(`${API}/schedule/${scheduleId}/update-hours`, data, { withCredentials: true })
      ));
      setEditedHours({});
      fetchData();
    } catch (error) {
      console.error('Failed to save hours:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingHours(false);
    }
  };

  // Calculate total hours between start and end (HH:MM format)
  const calcTotalHours = (startStr, endStr) => {
    if (!startStr || !endStr) return '-';
    const [sh, sm] = startStr.split(':').map(Number);
    const [eh, em] = endStr.split(':').map(Number);
    if (isNaN(sh) || isNaN(sm) || isNaN(eh) || isNaN(em)) return '-';
    let totalMin = (eh * 60 + em) - (sh * 60 + sm);
    if (totalMin < 0) totalMin += 24 * 60; // Handle overnight
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${h}h${m.toString().padStart(2, '0')}`;
  };

  // Today's totals - IMPORTANT: Only count approved missions
  const getTodayTotals = () => {
    const todaySchedules = schedules.filter(s => 
      s.date === today && isOperationalSchedule(s)
    );
    const totals = { delivered: 0, returned: 0, picked: 0, nonCollected: 0 };
    
    todaySchedules.forEach(s => {
      const stats = getDeliveryStats(s);
      totals.delivered += stats.delivered;
      totals.returned += stats.returned;
      totals.picked += stats.picked;
      totals.nonCollected += stats.nonCollected;
    });
    
    return totals;
  };

  // Present today
  const getPresentToday = () => {
    return schedules.filter((s) => s.date === today && isOperationalSchedule(s)).length;
  };

  // Open edit modal for a schedule
  const openEditModal = (schedule) => {
    const stats = getDeliveryStats(schedule);
    setEditModal({
      open: true,
      schedule: schedule,
      delivered: stats.delivered,
      returned: stats.returned,
      picked: stats.picked,
      vehiclePlate: schedule.vehicle_plate || '',
      pdaId: schedule.pda_id || '',
      saving: false,
      isNew: false
    });
  };

  // Open modal to CREATE a new mission (for employees who forgot to validate)
  const openCreateModal = (userId, userName, date) => {
    setEditModal({
      open: true,
      schedule: {
        user_id: userId,
        user_name: userName,
        date: date
      },
      delivered: 0,
      returned: 0,
      picked: 0,
      vehiclePlate: '',
      pdaId: '',
      saving: false,
      isNew: true,
      fromUnclosedClose: false
    });
  };

  const openUnclosedCloseModal = (mission) => {
    setEditModal({
      open: true,
      schedule: {
        user_id: mission.user_id,
        user_name: mission.user_name,
        date: mission.date || today,
        photo_session_id: mission.photo_session_id || null
      },
      delivered: 0,
      returned: 0,
      picked: 0,
      vehiclePlate: '',
      pdaId: '',
      saving: false,
      deleting: false,
      isNew: true,
      fromUnclosedClose: true
    });
  };

  // Close edit modal
  const closeEditModal = () => {
    setClosingMissionUserId(null);
    setEditModal({
      open: false,
      schedule: null,
      delivered: 0,
      returned: 0,
      picked: 0,
      vehiclePlate: '',
      pdaId: '',
      saving: false,
      isNew: false,
      fromUnclosedClose: false
    });
  };

  // Save delivery stats modification OR create new mission
  const saveDeliveryStats = async () => {
    if (!editModal.schedule) return;
    
    // At least one value must be filled
    if (editModal.delivered === 0 && editModal.returned === 0 && editModal.picked === 0) {
      alert('Veuillez renseigner au moins une valeur (livrés, retours ou collectés)');
      return;
    }
    
    setEditModal(prev => ({ ...prev, saving: true }));
    
    try {
      const schedule = editModal.schedule;
      
      if (editModal.isNew) {
        if (editModal.fromUnclosedClose) {
          const idempotencyKey = `cp-close-${schedule.user_id}-${schedule.date}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          await axios.post(
            `${API}/missions/unclosed/close`,
            {
              user_id: schedule.user_id,
              date: schedule.date,
              photo_session_id: schedule.photo_session_id || undefined,
              idempotency_key: idempotencyKey,
              delivered: editModal.delivered,
              returned: editModal.returned,
              non_collected: 0,
              picked: editModal.picked,
              vehicle_plate: editModal.vehiclePlate,
              pda_id: editModal.pdaId,
              admin_note: `Clôture administrative RJ CP d'une mission démarrée non terminée`,
              force_site_type: 'colis_prive'
            },
            {
              withCredentials: true,
              headers: {
                'X-Idempotency-Key': idempotencyKey
              }
            }
          );
        } else {
          // CREATE a new mission - force site_type to colis_prive so it appears in RJ CP
          await axios.post(
            `${API}/schedule/create-mission`,
            {
              user_id: schedule.user_id,
              date: schedule.date,
              delivered: editModal.delivered,
              returned: editModal.returned,
              picked: editModal.picked,
              vehicle_plate: editModal.vehiclePlate,
              pda_id: editModal.pdaId,
              admin_note: `Mission créée manuellement par admin (employé n'a pas validé)`,
              force_site_type: 'colis_prive'
            },
            { withCredentials: true }
          );
        }
      } else if (schedule.schedule_id) {
        // EDIT existing mission by schedule_id
        await axios.put(
          `${API}/schedule/${schedule.schedule_id}/edit-before-approval`,
          {
            delivered: editModal.delivered,
            returned: editModal.returned,
            picked: editModal.picked,
            vehicle_plate: editModal.vehiclePlate,
            pda_id: editModal.pdaId,
            admin_note: `Modification directe RJ CP`
          },
          { withCredentials: true }
        );
      } else {
        // Fallback: Use user_id + date to update via a different endpoint
        await axios.put(
          `${API}/schedule/update-by-user-date`,
          {
            user_id: schedule.user_id,
            date: schedule.date,
            delivered: editModal.delivered,
            returned: editModal.returned,
            picked: editModal.picked,
            vehicle_plate: editModal.vehiclePlate,
            pda_id: editModal.pdaId,
            admin_note: `Modification directe RJ CP`
          },
          { withCredentials: true }
        );
      }
      
      // Refresh data
      await fetchData();
      await fetchUnclosedMissions();
      
      closeEditModal();
      alert(`✅ Données modifiées avec succès`);
      
    } catch (error) {
      console.error('Failed to save:', error);
      alert(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setEditModal(prev => ({ ...prev, saving: false }));
    }
  };

  // Delete mission completely
  const deleteMission = async () => {
    if (!editModal.schedule) return;
    
    const schedule = editModal.schedule;
    const stats = getDeliveryStats(schedule);
    const confirmMsg = `⚠️ Êtes-vous sûr de vouloir SUPPRIMER cette mission ?\n\n` +
      `Employé: ${schedule.user_name || 'Inconnu'}\n` +
      `Date: ${schedule.date}\n` +
      `Livrés: ${stats.delivered} | Retours: ${stats.returned} | Collectés: ${stats.picked}\n\n` +
      `Cette action est IRRÉVERSIBLE et supprimera également:\n` +
      `- L'historique véhicule associé\n` +
      `- La mission visible par l'employé`;
    
    if (!window.confirm(confirmMsg)) return;
    
    setEditModal(prev => ({ ...prev, deleting: true }));
    
    try {
      if (schedule.schedule_id) {
        await axios.delete(
          `${API}/schedule/${schedule.schedule_id}`,
          { withCredentials: true }
        );
      } else {
        // Fallback: delete by user_id + date
        await axios.delete(
          `${API}/schedule/delete-by-user-date/${schedule.user_id}/${schedule.date}`,
          { withCredentials: true }
        );
      }
      
      // Refresh data
      await fetchData();
      
      closeEditModal();
      alert(`✅ Mission supprimée avec succès`);
      
    } catch (error) {
      console.error('Failed to delete:', error);
      alert(`❌ Erreur: ${error.response?.data?.detail || error.message}`);
    } finally {
      setEditModal(prev => ({ ...prev, deleting: false }));
    }
  };

  useEmployeeActivityRefresh(fetchData);
  const schedulesBySite = getSchedulesBySite();
  const datesInRange = getDatesInRange();
  const activeRange = getDateRange();
  const start = activeRange?.start || '';
  const end = activeRange?.end || '';
  const todayTotals = getTodayTotals();
  const presentToday = getPresentToday();

  // Grand totals - IMPORTANT: Only count non-rejected missions
  const grandTotals = { delivered: 0, returned: 0, picked: 0, nonCollected: 0 };
  schedules
    .filter((s) => isOperationalSchedule(s))
    .forEach(s => {
      const stats = getDeliveryStats(s);
      grandTotals.delivered += stats.delivered;
      grandTotals.returned += stats.returned;
      grandTotals.picked += stats.picked;
      grandTotals.nonCollected += stats.nonCollected;
    });

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' });
  
  const formatPeriod = () => {
    if (viewMode === 'custom') {
      const range = getDateRange();
      if (!range) return 'Période personnalisée (à définir)';
      return `Période personnalisée du ${new Date(range.start).toLocaleDateString('fr-FR')} au ${new Date(range.end).toLocaleDateString('fr-FR')}`;
    }
    if (viewMode === 'day') return currentDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (viewMode === 'week') return `Semaine du ${new Date(start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} au ${new Date(end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
    return currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const incompleteWarningMessage = "Données d'exploitation incomplètes. Merci de compléter les informations de prestation de cette journée.";

  const toggleWarningDetails = (warningKey) => {
    setExpandedWarnings((prev) => ({
      ...prev,
      [warningKey]: !prev[warningKey]
    }));
  };

  const getEmployeeDisplayNameById = (userId) => {
    const emp = getEmployeeInfo(userId);
    return formatEmployeeName(emp) || 'Inconnu';
  };

  const userMatchesSearch = (userId) => {
    const term = employeeSearch.trim().toLowerCase();
    if (!term) return true;
    const emp = getEmployeeInfo(userId);
    const fullName = formatEmployeeName(emp).toLowerCase();
    const firstName = (emp?.first_name || '').toLowerCase();
    const lastName = (emp?.last_name || '').toLowerCase();
    return fullName.includes(term) || firstName.includes(term) || lastName.includes(term);
  };

  const getUserFilterFlags = (userId, siteSchedules = null) => {
    const sourceSchedules = (siteSchedules || schedules).filter((s) => s.user_id === userId);
    let hasWorked = false;
    let hasAbsent = false;
    let hasIncomplete = false;
    let hasValidatedMission = false;

    datesInRange.forEach((date) => {
      const schedule = sourceSchedules.find((s) => s.date === date);
      const simpleStatus = getSimpleRjStatus(schedule, date);
      if (simpleStatus.key === 'travaille') {
        hasWorked = true;
      }
      if ((schedule?.status || '').toLowerCase() === 'absent') {
        hasAbsent = true;
      }
      if (simpleStatus.key === 'travaille' && schedule && !hasOperationalDataInSchedule(schedule)) {
        hasIncomplete = true;
      }
      if ((schedule?.validation_status || '').toLowerCase() === 'approved') {
        hasValidatedMission = true;
      }
    });

    const hasPlannedEntries = sourceSchedules.length > 0;
    return { hasWorked, hasAbsent, hasIncomplete, hasPlannedEntries, hasValidatedMission };
  };

  const userMatchesQuickFilter = (userId, siteSchedules = null) => {
    const { hasWorked, hasAbsent, hasIncomplete, hasValidatedMission } = getUserFilterFlags(userId, siteSchedules);

    if (quickFilter === 'to_complete') return hasIncomplete;
    if (quickFilter === 'worked') return hasWorked;
    if (quickFilter === 'absent') return hasAbsent;
    if (quickFilter === 'no_activity') return !hasWorked && !hasValidatedMission;
    return true;
  };

  const shouldDisplayUser = (userId, siteSchedules = null) => {
    if (selectedEmployeeId !== 'all' && userId !== selectedEmployeeId) {
      return false;
    }
    if (!userMatchesQuickFilter(userId, siteSchedules)) {
      return false;
    }
    return true;
  };

  const eligibleEmployeeOptions = useMemo(() => {
    const ids = new Set(employees.map((e) => e.user_id));
    return Array.from(ids)
      .filter((id) => userMatchesQuickFilter(id, schedules))
      .map((id) => ({ user_id: id, label: getEmployeeDisplayNameById(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [employees, schedules, quickFilter]);

  const visibleEmployeeOptions = useMemo(() => {
    const term = employeeSearch.trim().toLowerCase();
    if (!term) return eligibleEmployeeOptions;
    return eligibleEmployeeOptions.filter((item) => item.label.toLowerCase().includes(term));
  }, [eligibleEmployeeOptions, employeeSearch]);

  useEffect(() => {
    if (selectedEmployeeId === 'all') return;
    if (!eligibleEmployeeOptions.some((item) => item.user_id === selectedEmployeeId)) {
      setSelectedEmployeeId('all');
    }
  }, [eligibleEmployeeOptions, selectedEmployeeId]);

  const selectedEmployeeIndex = useMemo(() => {
    if (selectedEmployeeId === 'all') return -1;
    return eligibleEmployeeOptions.findIndex((item) => item.user_id === selectedEmployeeId);
  }, [eligibleEmployeeOptions, selectedEmployeeId]);

  const goToPreviousEmployee = () => {
    if (selectedEmployeeIndex <= 0) return;
    setSelectedEmployeeId(eligibleEmployeeOptions[selectedEmployeeIndex - 1].user_id);
  };

  const goToNextEmployee = () => {
    if (selectedEmployeeIndex < 0 || selectedEmployeeIndex >= eligibleEmployeeOptions.length - 1) return;
    setSelectedEmployeeId(eligibleEmployeeOptions[selectedEmployeeIndex + 1].user_id);
  };

  const filteredEmployeeIdSetForExport = useMemo(() => {
    const ids = eligibleEmployeeOptions.map((item) => item.user_id);
    if (selectedEmployeeId !== 'all') {
      return new Set(ids.filter((id) => id === selectedEmployeeId));
    }
    return new Set(ids);
  }, [eligibleEmployeeOptions, selectedEmployeeId]);

  const quickFilterMeta = useMemo(() => {
    const ids = new Set(employees.map((e) => e.user_id));
    const counters = {
      all: 0,
      to_complete: 0,
      worked: 0,
      absent: 0,
      no_activity: 0,
    };

    Array.from(ids).forEach((userId) => {
      const { hasWorked, hasAbsent, hasIncomplete, hasValidatedMission } = getUserFilterFlags(userId, schedules);
      counters.all += 1;
      if (hasIncomplete) counters.to_complete += 1;
      if (hasWorked) counters.worked += 1;
      if (hasAbsent) counters.absent += 1;
      if (!hasWorked && !hasValidatedMission) counters.no_activity += 1;
    });

    return {
      all: { label: 'Tous', count: counters.all },
      to_complete: { label: 'RJ incomplets', count: counters.to_complete },
      worked: { label: 'Ont travaillé', count: counters.worked },
      absent: { label: 'Absents', count: counters.absent },
      no_activity: { label: 'Aucune activité enregistrée', count: counters.no_activity },
    };
  }, [employees, schedules, datesInRange]);

  const activeQuickFilterMeta = quickFilterMeta[quickFilter] || quickFilterMeta.all;
  const selectedEmployeeLabel = selectedEmployeeId === 'all'
    ? 'Tous les salariés'
    : (eligibleEmployeeOptions.find((item) => item.user_id === selectedEmployeeId)?.label || 'Tous les salariés');

  useEffect(() => {
    const onOutsideClick = (event) => {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target)) {
        setEmployeeDropdownOpen(false);
      }
      if (quickFilterDropdownRef.current && !quickFilterDropdownRef.current.contains(event.target)) {
        setQuickFilterDropdownOpen(false);
      }
    };

    const onEsc = (event) => {
      if (event.key === 'Escape') {
        setEmployeeDropdownOpen(false);
        setQuickFilterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', onOutsideClick);
    document.addEventListener('touchstart', onOutsideClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
      document.removeEventListener('touchstart', onOutsideClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  return (
    <Layout user={user} activeModule={activeModule}>
      <div className="space-y-4 fade-in">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">CP - Colis Prive</h1>
          <p className="text-gray-500">Livraisons, retours et collectes par chauffeur</p>
        </div>

        {/* Compact sticky command bar */}
        <Card className="sticky top-16 z-40 border-slate-200 shadow-sm" data-testid="cp-rj-sticky-command-bar">
          <CardContent className="p-3 space-y-3 bg-white/95 backdrop-blur">
            <RjActivityFilter activity={activity} setActivity={setActivity} prefix="cp" />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={activeMainTab === 'rj' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveMainTab('rj')}
                data-testid="cp-main-tab-rj"
              >
                RJ
              </Button>
              <Button
                variant={activeMainTab === 'missions_to_close' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveMainTab('missions_to_close')}
                data-testid="cp-main-tab-missions-to-close"
              >
                Missions à clôturer ({unclosedMissions.length})
              </Button>
              <div className="h-6 w-px bg-slate-200 mx-1" />
              {['day', 'week', 'month', 'custom'].map(mode => (
                <Button key={mode} variant={viewMode === mode ? 'default' : 'outline'} size="sm" onClick={() => setViewMode(mode)} data-testid={`cp-view-mode-${mode}`}>
                  {mode === 'day' ? 'Jour' : mode === 'week' ? 'Semaine' : mode === 'month' ? 'Mois' : 'Période'}
                </Button>
              ))}
              <div className="h-6 w-px bg-slate-200 mx-1" />
              {['simple', 'detail'].map((mode) => (
                <Button
                  key={mode}
                  variant={rjDisplayMode === mode ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setRjDisplayMode(mode)}
                  data-testid={`cp-rj-display-${mode}`}
                >
                  {mode === 'simple' ? 'Vue simplifiée' : 'Vue détaillée'}
                </Button>
              ))}
              <Button variant="outline" size="sm" onClick={() => exportToExcel('filtered')} className="ml-auto text-green-700 border-green-300 hover:bg-green-50" data-testid="export-excel-cp-filtered">
                <Download className="w-4 h-4 mr-1" /> Exporter la vue filtrée
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportToExcel('all')} className="text-green-700 border-green-300 hover:bg-green-50" data-testid="export-excel-cp-all">
                <Download className="w-4 h-4 mr-1" /> Exporter toutes les données
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} disabled={viewMode === 'custom'} data-testid="cp-period-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
              <p className="font-semibold text-[#0F172A] min-w-[280px] text-center">{formatPeriod()}</p>
              <Button variant="ghost" size="sm" onClick={() => navigate(1)} disabled={viewMode === 'custom'} data-testid="cp-period-next-btn"><ChevronRight className="w-4 h-4" /></Button>

              <div className="h-6 w-px bg-slate-200 mx-1" />

              <div className="relative" ref={employeeDropdownRef}>
                <button
                  type="button"
                  className="h-9 min-w-[270px] max-w-[320px] border rounded-md px-3 text-sm bg-white flex items-center justify-between gap-2"
                  onClick={() => {
                    setEmployeeDropdownOpen((v) => !v);
                    setQuickFilterDropdownOpen(false);
                  }}
                  data-testid="cp-rj-employee-dropdown-trigger"
                  aria-expanded={employeeDropdownOpen}
                >
                  <span className="truncate"><strong>Salarié :</strong> {selectedEmployeeLabel}</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${employeeDropdownOpen ? 'rotate-90' : ''}`} />
                </button>

                {employeeDropdownOpen && (
                  <div className="absolute top-10 left-0 z-50 w-[320px] max-w-[85vw] bg-white border rounded-md shadow-lg p-2" data-testid="cp-rj-employee-dropdown-menu">
                    <div className="relative mb-2">
                      <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                        placeholder="Rechercher nom / prénom"
                        className="w-full h-9 pl-8 pr-3 border rounded-md text-sm"
                        data-testid="cp-rj-employee-search-inside-dropdown"
                      />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto overscroll-contain pr-1" data-testid="cp-rj-employee-dropdown-scroll">
                      <button
                        type="button"
                        className={`w-full text-left px-2 py-2 rounded text-sm ${selectedEmployeeId === 'all' ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-slate-50'}`}
                        onClick={() => {
                          setSelectedEmployeeId('all');
                          setEmployeeDropdownOpen(false);
                        }}
                        data-testid="cp-rj-employee-option-all"
                      >
                        Tous les salariés
                      </button>
                      {visibleEmployeeOptions.map((item) => (
                        <button
                          key={item.user_id}
                          type="button"
                          className={`w-full text-left px-2 py-2 rounded text-sm ${selectedEmployeeId === item.user_id ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-slate-50'}`}
                          onClick={() => {
                            setSelectedEmployeeId(item.user_id);
                            setEmployeeDropdownOpen(false);
                          }}
                          data-testid={`cp-rj-employee-option-${item.user_id}`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={quickFilterDropdownRef}>
                <button
                  type="button"
                  className="h-9 min-w-[240px] max-w-[320px] border rounded-md px-3 text-sm bg-white flex items-center justify-between gap-2"
                  onClick={() => {
                    setQuickFilterDropdownOpen((v) => !v);
                    setEmployeeDropdownOpen(false);
                  }}
                  data-testid="cp-rj-filter-dropdown-trigger"
                  aria-expanded={quickFilterDropdownOpen}
                >
                  <span className="truncate">Filtrer l’affichage : {activeQuickFilterMeta.label} ({activeQuickFilterMeta.count})</span>
                  <div className="flex items-center gap-1">
                    {quickFilter !== 'all' && (
                      <span
                        className="text-slate-400 hover:text-slate-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuickFilter('all');
                        }}
                        data-testid="cp-rj-clear-quick-filter"
                      >
                        <X className="w-3 h-3" />
                      </span>
                    )}
                    <ChevronRight className={`w-4 h-4 transition-transform ${quickFilterDropdownOpen ? 'rotate-90' : ''}`} />
                  </div>
                </button>

                {quickFilterDropdownOpen && (
                  <div className="absolute top-10 left-0 z-50 w-[320px] max-w-[85vw] bg-white border rounded-md shadow-lg p-2" data-testid="cp-rj-filter-dropdown-menu">
                    <div className="max-h-[300px] overflow-y-auto overscroll-contain pr-1" data-testid="cp-rj-filter-dropdown-scroll">
                      {['all', 'to_complete', 'worked', 'absent', 'no_activity'].map((key) => {
                        const meta = quickFilterMeta[key];
                        return (
                          <button
                            key={key}
                            type="button"
                            className={`w-full text-left px-2 py-2 rounded text-sm flex items-center justify-between ${quickFilter === key ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-slate-50'}`}
                            onClick={() => {
                              setQuickFilter(key);
                              setQuickFilterDropdownOpen(false);
                            }}
                            data-testid={`cp-rj-filter-option-${key}`}
                          >
                            <span>{meta.label}</span>
                            <span className="text-xs text-slate-500">{meta.count}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <Button variant="outline" size="sm" onClick={goToPreviousEmployee} disabled={selectedEmployeeId === 'all' || selectedEmployeeIndex <= 0} data-testid="cp-rj-employee-prev">
                <ChevronLeft className="w-4 h-4 mr-1" /> Salarié précédent
              </Button>
              <Button variant="outline" size="sm" onClick={goToNextEmployee} disabled={selectedEmployeeId === 'all' || selectedEmployeeIndex === -1 || selectedEmployeeIndex >= eligibleEmployeeOptions.length - 1} data-testid="cp-rj-employee-next">
                Salarié suivant <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {activeMainTab === 'missions_to_close' && (
          <Card data-testid="cp-missions-to-close-card">
            <CardHeader className="bg-amber-50">
              <CardTitle className="text-base flex items-center justify-between">
                <span>Clôture administrative - missions démarrées non terminées</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchUnclosedMissions}
                  disabled={loadingUnclosed}
                  data-testid="cp-missions-to-close-refresh-button"
                >
                  {loadingUnclosed ? 'Actualisation...' : 'Actualiser'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {loadingUnclosed ? (
                <p className="text-sm text-slate-500" data-testid="cp-missions-to-close-loading">Chargement des missions en cours…</p>
              ) : unclosedMissions.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="cp-missions-to-close-empty">Aucune mission CP à clôturer actuellement.</p>
              ) : (
                <div className="space-y-2" data-testid="cp-missions-to-close-list">
                  {unclosedMissions.map((mission) => (
                    <div
                      key={`${mission.user_id}-${mission.start_time}`}
                      className="border rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                      data-testid={`cp-missions-to-close-item-${mission.user_id}`}
                    >
                      <div className="space-y-1">
                        <p className="font-semibold text-slate-800">{mission.user_name || 'Inconnu'}</p>
                        <p className="text-xs text-slate-600">Site: {mission.site_name || '-'}</p>
                        <p className="text-xs text-slate-600">Début: {mission.start_time || '-'} • {mission.elapsed_hours || 0}h</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          setClosingMissionUserId(mission.user_id);
                          openUnclosedCloseModal(mission);
                        }}
                        data-testid={`cp-missions-to-close-action-${mission.user_id}`}
                      >
                        {closingMissionUserId === mission.user_id ? 'Clôture...' : 'Clôturer maintenant'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeMainTab === 'rj' && (
        <div className="space-y-4" data-testid="cp-rj-tab-content">
        {viewMode === 'custom' && (
          <Card data-testid="cp-custom-range-card">
            <CardContent className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600">Date début</label>
                <input
                  type="date"
                  value={customRange.start}
                  onChange={(e) => setCustomRange((prev) => ({ ...prev, start: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="cp-custom-range-start"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Date fin</label>
                <input
                  type="date"
                  value={customRange.end}
                  onChange={(e) => setCustomRange((prev) => ({ ...prev, end: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                  data-testid="cp-custom-range-end"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Today's Summary */}
        <Card className="bg-gradient-to-r from-purple-600 to-purple-800 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-5 h-5" />
              <span className="font-semibold">Aujourd&apos;hui ({new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })})</span>
              <span className="ml-auto bg-white/20 px-3 py-1 rounded-full text-sm">{presentToday} mission(s)</span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center bg-white/10 rounded p-3">
                <p className="text-xs opacity-80">Livrés</p>
                <p className="text-2xl font-bold">{todayTotals.delivered}</p>
              </div>
              <div className="text-center bg-white/10 rounded p-3">
                <p className="text-xs opacity-80">Retours</p>
                <p className="text-2xl font-bold">{todayTotals.returned}</p>
              </div>
              <div className="text-center bg-white/10 rounded p-3">
                <p className="text-xs opacity-80">Non Coll.</p>
                <p className="text-2xl font-bold">{todayTotals.nonCollected}</p>
              </div>
              <div className="text-center bg-white/10 rounded p-3">
                <p className="text-xs opacity-80">Collectes</p>
                <p className="text-2xl font-bold">{todayTotals.picked}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Period Summary */}
        <div className="text-xs font-semibold text-slate-600" data-testid="cp-rj-global-totals-label">Totaux globaux</div>
        <div className="grid grid-cols-4 gap-2">
          <Card className="bg-green-500 text-white">
            <CardContent className="p-4 text-center">
              <p className="text-xs opacity-80">Total Livrés</p>
              <p className="text-2xl font-bold">{grandTotals.delivered}</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-500 text-white">
            <CardContent className="p-4 text-center">
              <p className="text-xs opacity-80">Total Retours</p>
              <p className="text-2xl font-bold">{grandTotals.returned}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-500 text-white">
            <CardContent className="p-4 text-center">
              <p className="text-xs opacity-80">Non Collectés</p>
              <p className="text-2xl font-bold">{grandTotals.nonCollected}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500 text-white">
            <CardContent className="p-4 text-center">
              <p className="text-xs opacity-80">Total Collectes</p>
              <p className="text-2xl font-bold">{grandTotals.picked}</p>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F172A]"></div>
          </div>
        ) : (
          Object.entries(schedulesBySite)
            .filter(([_, data]) => data.users.size > 0 || sites.find(s => s.site_id === _))
            .sort((a, b) => a[1].site_name.localeCompare(b[1].site_name))
            .map(([siteId, siteData]) => {
              // Get unique users and sort them alphabetically by last name
              const uniqueUserIds = Array.from(siteData.users);
              const uniqueUsersWithInfo = uniqueUserIds.map(id => ({
                userId: id,
                emp: getEmployeeInfo(id)
              }));
              // Sort by last name, then first name
              uniqueUsersWithInfo.sort((a, b) => {
                const lastNameA = (a.emp?.last_name || a.emp?.name || '').toLowerCase();
                const lastNameB = (b.emp?.last_name || b.emp?.name || '').toLowerCase();
                if (lastNameA !== lastNameB) return lastNameA.localeCompare(lastNameB);
                const firstNameA = (a.emp?.first_name || '').toLowerCase();
                const firstNameB = (b.emp?.first_name || '').toLowerCase();
                return firstNameA.localeCompare(firstNameB);
              });
              const uniqueUsers = uniqueUsersWithInfo.map(u => u.userId);
              const visibleUsers = uniqueUsers.filter((userId) => shouldDisplayUser(userId, siteData.schedules));
              
              const siteTotals = { delivered: 0, returned: 0, picked: 0, nonCollected: 0 };
              // IMPORTANT: Only count administratively approved worked missions
              siteData.schedules
                .filter((s) => isOperationalSchedule(s))
                .forEach(s => {
                  const stats = getDeliveryStats(s);
                  siteTotals.delivered += stats.delivered;
                  siteTotals.returned += stats.returned;
                  siteTotals.picked += stats.picked;
                  siteTotals.nonCollected += stats.nonCollected;
                });
              
              return (
                <Card key={siteId} className="border-l-4 border-l-purple-500">
                  <CardHeader className="py-3 px-4 bg-purple-50">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        {siteData.site_name}
                      </span>
                      <span className="text-sm font-normal text-gray-500">{visibleUsers.length} chauffeur(s) affiché(s) - {siteData.schedules.length} mission(s)</span>
                    </CardTitle>
                  </CardHeader>
                  
                  {visibleUsers.length === 0 ? (
                    <CardContent className="p-0">
                      {/* Show empty calendar with days */}
                      {viewMode !== 'day' && (
                        <div className="overflow-auto max-h-[60vh]" data-testid={`cp-site-table-scroll-empty-${siteId}`}>
                        <table className="w-max min-w-full text-sm">
                          <thead className="sticky top-0 z-40">
                            <tr className="border-b bg-purple-50">
                              <th className="text-left p-2 font-medium sticky left-0 z-30 bg-purple-50 min-w-[170px]">Jours du mois</th>
                              {datesInRange.map(d => (
                                <th key={d} className={`p-2 text-center font-medium text-xs min-w-[40px] ${d === today ? 'bg-purple-200' : ''}`}>
                                  {formatDate(d)}
                                </th>
                              ))}
                              <th className="p-2 text-center font-medium bg-green-500 text-white sticky right-[216px] z-30 min-w-[72px]">Livrés</th>
                              <th className="p-2 text-center font-medium bg-orange-500 text-white sticky right-[144px] z-30 min-w-[72px]">Retours</th>
                              <th className="p-2 text-center font-medium bg-red-500 text-white sticky right-[72px] z-30 min-w-[72px]">Non coll.</th>
                              <th className="p-2 text-center font-medium bg-blue-500 text-white sticky right-0 z-30 min-w-[72px]">Ramassés</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="p-2 text-gray-400 italic sticky left-0 z-10 bg-white">Aucun chauffeur</td>
                              {datesInRange.map(d => (
                                <td key={d} className={`p-2 text-center text-gray-300 ${d === today ? 'bg-purple-50' : ''}`}>-</td>
                              ))}
                              <td className="p-2 text-center text-gray-300 sticky right-[216px] z-10 bg-white">0</td>
                              <td className="p-2 text-center text-gray-300 sticky right-[144px] z-10 bg-white">0</td>
                              <td className="p-2 text-center text-gray-300 sticky right-[72px] z-10 bg-white">0</td>
                              <td className="p-2 text-center text-gray-300 sticky right-0 z-10 bg-white">0</td>
                            </tr>
                          </tbody>
                        </table>
                        </div>
                      )}
                      {viewMode === 'day' && (
                        <div className="p-4 text-center text-gray-400">Aucune mission</div>
                      )}
                    </CardContent>
                  ) : (
                    <CardContent className="p-0">
                      <div className="md:hidden space-y-3 p-3" data-testid={`cp-rj-mobile-site-${siteId}`}>
                        {visibleUsers.map((userId) => {
                          const emp = getEmployeeInfo(userId);
                          const userName = formatEmployeeName(emp) || siteData.schedules.find(s => s.user_id === userId)?.user_name || 'Inconnu';
                          const userStatsMobile = getUserStats(userId, siteData.schedules);
                          return (
                            <div key={`mobile-${userId}`} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid={`cp-rj-mobile-driver-${userId}`}>
                              <p className="font-semibold text-slate-800">{userName}</p>
                              <div className="space-y-2">
                                {datesInRange.map((d) => {
                                  const userSchedule = siteData.schedules.find(s => s.user_id === userId && s.date === d);
                                  const dayData = userStatsMobile.byDate[d];
                                  const simpleStatus = getSimpleRjStatus(userSchedule, d);
                                  const details = getCpMissionDetails(userSchedule, dayData);
                                  const showOperationalWarning = simpleStatus.key === 'travaille' && userSchedule && !hasOperationalDataInSchedule(userSchedule);
                                  return (
                                    <div key={`mobile-${userId}-${d}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-2.5 py-2">
                                      <div className="min-w-0">
                                        <p className="text-xs font-semibold text-slate-700">{formatDate(d)}</p>
                                        <p className="text-xs text-slate-600 truncate">{userSchedule?.tour_type || 'Mission CP'}</p>
                                        {dayData && (
                                          <p className="text-[11px] text-slate-500 truncate">L {dayData.delivered || 0} · R {dayData.returned || 0} · C {dayData.picked || 0}</p>
                                        )}
                                        {details.length > 0 && (
                                          <p className="text-[11px] text-indigo-600 truncate" data-testid={`cp-rj-mobile-details-${userId}-${d}`}>
                                            {details[0]}
                                          </p>
                                        )}
                                        {showOperationalWarning && (
                                          rjDisplayMode === 'simple' ? (
                                            <div className="mt-1">
                                              <button
                                                type="button"
                                                onClick={() => toggleWarningDetails(`cp-mobile-${userId}-${d}`)}
                                                className="inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold bg-amber-100 text-amber-800 border-amber-300"
                                                data-testid={`cp-rj-incomplete-badge-mobile-${userId}-${d}`}
                                              >
                                                Incomplet
                                              </button>
                                              {expandedWarnings[`cp-mobile-${userId}-${d}`] && (
                                                <p className="text-[11px] text-amber-700 font-medium leading-tight mt-1" data-testid={`cp-rj-operational-warning-mobile-${userId}-${d}`}>
                                                  {incompleteWarningMessage}
                                                </p>
                                              )}
                                            </div>
                                          ) : (
                                            <p className="text-[11px] text-amber-700 font-medium leading-tight" data-testid={`cp-rj-operational-warning-mobile-${userId}-${d}`}>
                                              ⚠️ {incompleteWarningMessage}
                                            </p>
                                          )
                                        )}
                                      </div>
                                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${getSimpleStatusClass(simpleStatus.key)}`}>
                                        {simpleStatus.label}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="hidden md:block overflow-auto max-h-[60vh]" data-testid={`cp-site-table-scroll-${siteId}`}>
                      <table className="w-max min-w-full text-sm">
                        <thead className="sticky top-0 z-40">
                          <tr className="border-b bg-gray-50">
                            <th className="text-left p-2 font-medium sticky left-0 z-30 bg-gray-50 min-w-[170px]">Chauffeur</th>
                            {viewMode !== 'day' && datesInRange.map(d => (
                              <th key={d} className={`p-2 text-center font-medium text-xs min-w-[60px] ${d === today ? 'bg-purple-100' : ''}`}>
                                {formatDate(d)}
                              </th>
                            ))}
                            <th className="p-2 text-center font-medium bg-green-500 text-white sticky right-[216px] z-30 min-w-[72px]">Livrés</th>
                            <th className="p-2 text-center font-medium bg-orange-500 text-white sticky right-[144px] z-30 min-w-[72px]">Retours</th>
                            <th className="p-2 text-center font-medium bg-red-500 text-white sticky right-[72px] z-30 min-w-[72px]">Non coll.</th>
                            <th className="p-2 text-center font-medium bg-blue-500 text-white sticky right-0 z-30 min-w-[72px]">Ramassés</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleUsers.map(userId => {
                            const emp = getEmployeeInfo(userId);
                            const userName = formatEmployeeName(emp) || siteData.schedules.find(s => s.user_id === userId)?.user_name || 'Inconnu';
                            const { totals, byDate } = getUserStats(userId, siteData.schedules);
                            
                            return (
                              <tr key={userId} className={`border-b hover:bg-gray-50 ${rjDisplayMode === 'simple' ? 'h-11' : ''}`}>
                                <td className="p-2 sticky left-0 z-20 bg-white">{userName}</td>
                                {viewMode !== 'day' && datesInRange.map(d => {
                                  const dayData = byDate[d];
                                  const hasData = dayData && (dayData.delivered > 0 || dayData.returned > 0 || dayData.picked > 0 || dayData.nonCollected > 0);
                                  const userSchedule = siteData.schedules.find(s => s.user_id === userId && s.date === d);
                                  const simpleStatus = getSimpleRjStatus(userSchedule, d);
                                  const showPrestationCounter = hasData && userSchedule && canShowPrestationBadge(simpleStatus.key);
                                  const showOperationalWarning = simpleStatus.key === 'travaille' && userSchedule && !hasOperationalDataInSchedule(userSchedule);
                                  const hasComment = !!(userSchedule?.employee_comment || userSchedule?.comment);
                                  const missionDetails = getCpMissionDetails(userSchedule, dayData);
                                  const hasDetails = missionDetails.length > 0;
                                  return (
                                    <td key={d} className={`p-2 text-center text-xs ${d === today ? 'bg-purple-50' : ''}`}>
                                      {showPrestationCounter ? (
                                        <TooltipProvider delayDuration={100}>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <button 
                                                onClick={() => openEditModal(userSchedule)}
                                                className="space-y-0.5 cursor-pointer hover:bg-purple-100 rounded p-1 transition-colors w-full group relative"
                                              >
                                                <div className="text-green-600 font-semibold">{dayData.delivered}</div>
                                                <div className="text-orange-500">{dayData.returned}</div>
                                                <div className="text-blue-500">{dayData.picked}</div>
                                                <Edit2 className="w-2.5 h-2.5 absolute -top-0.5 -right-0.5 opacity-0 group-hover:opacity-100 bg-orange-500 text-white rounded-full p-0.5" />
                                              </button>
                                            </TooltipTrigger>
                                            <TooltipContent className="bg-gray-900 text-white">
                                              <p className="font-medium">Colis Privé</p>
                                              <p className="text-xs text-gray-300">Livrés: {dayData.delivered} | Retours: {dayData.returned} | Non coll.: {dayData.nonCollected} | Collectés: {dayData.picked}</p>
                                              {(dayData.start_time || dayData.end_time) && (
                                                <p className="text-xs text-gray-300">Horaires: {dayData.start_time || '?'} - {dayData.end_time || '?'}</p>
                                              )}
                                              {userSchedule?.vehicle_plate && (
                                                <p className="text-xs text-gray-300">Véhicule: {userSchedule.vehicle_plate}</p>
                                              )}
                                              {userSchedule?.pda_id && (
                                                <p className="text-xs text-gray-300">PDA: {pdas.find(p => p.pda_id === userSchedule.pda_id)?.numero || userSchedule.pda_id}</p>
                                              )}
                                              <p className="text-xs text-gray-300 mt-1">Cliquer pour modifier</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      ) : userSchedule ? (
                                        <TooltipProvider delayDuration={100}>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <button
                                                onClick={() => openEditModal(userSchedule)}
                                                className="inline-flex items-center justify-center w-7 h-7 bg-slate-200 text-slate-700 rounded-full text-xs hover:bg-slate-300 transition-colors"
                                                data-testid={`cp-rj-future-planned-${userId}-${d}`}
                                              >
                                                •
                                              </button>
                                            </TooltipTrigger>
                                            <TooltipContent className="bg-gray-900 text-white">
                                              <p className="text-xs">Mission planifiée (future non comptabilisée)</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      ) : (
                                        <TooltipProvider delayDuration={100}>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <button 
                                                onClick={() => openCreateModal(userId, userName, d)}
                                                className="inline-flex items-center justify-center w-7 h-7 text-gray-300 hover:bg-green-100 hover:text-green-600 rounded-full text-xs transition-colors border border-dashed border-gray-200 hover:border-green-400"
                                              >
                                                +
                                              </button>
                                            </TooltipTrigger>
                                            <TooltipContent className="bg-gray-900 text-white">
                                              <p className="text-xs">Cliquer pour ajouter une mission</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                      <div className="mt-1 flex flex-col items-center gap-1">
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${getSimpleStatusClass(simpleStatus.key)}`} data-testid={`cp-rj-status-${userId}-${d}`}>
                                          {simpleStatus.label}
                                        </span>
                                        {showOperationalWarning && (
                                          rjDisplayMode === 'simple' ? (
                                            <div className="flex flex-col items-center gap-1" data-testid={`cp-rj-operational-warning-desktop-${userId}-${d}`}>
                                              <button
                                                type="button"
                                                onClick={() => toggleWarningDetails(`cp-desktop-${userId}-${d}`)}
                                                className="inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold bg-amber-100 text-amber-800 border-amber-300"
                                                data-testid={`cp-rj-incomplete-badge-${userId}-${d}`}
                                              >
                                                Incomplet
                                              </button>
                                              {expandedWarnings[`cp-desktop-${userId}-${d}`] && (
                                                <span className="max-w-[120px] text-[10px] leading-tight text-amber-700 font-medium text-center">
                                                  {incompleteWarningMessage}
                                                </span>
                                              )}
                                            </div>
                                          ) : (
                                            <span className="max-w-[120px] text-[10px] leading-tight text-amber-700 font-medium text-center" data-testid={`cp-rj-operational-warning-desktop-${userId}-${d}`}>
                                              ⚠️ {incompleteWarningMessage}
                                            </span>
                                          )
                                        )}
                                        {hasComment && rjDisplayMode === 'detail' && (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold bg-indigo-100 text-indigo-700 border-indigo-300" data-testid={`cp-rj-comment-${userId}-${d}`}>
                                            Com
                                          </span>
                                        )}
                                        {hasDetails && rjDisplayMode === 'detail' && (
                                          <TooltipProvider delayDuration={100}>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <button
                                                  className="inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-semibold bg-violet-100 text-violet-700 border-violet-300"
                                                  data-testid={`cp-rj-details-button-${userId}-${d}`}
                                                >
                                                  Détails
                                                </button>
                                              </TooltipTrigger>
                                              <TooltipContent className="bg-slate-900 text-white max-w-[280px] text-left">
                                                <div className="space-y-1">
                                                  {missionDetails.map((line, idx) => (
                                                    <p key={`${line}-${idx}`} className="text-xs">• {line}</p>
                                                  ))}
                                                </div>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                      </div>
                                    </td>
                                  );
                                })}
                                <td className="p-2 text-center font-bold text-lg text-green-700 sticky right-[216px] z-10 bg-white">{totals.delivered}</td>
                                <td className="p-2 text-center font-bold text-lg text-orange-700 sticky right-[144px] z-10 bg-white">{totals.returned}</td>
                                <td className="p-2 text-center font-bold text-lg text-red-700 sticky right-[72px] z-10 bg-white">{totals.nonCollected}</td>
                                <td className="p-2 text-center font-bold text-lg text-blue-700 sticky right-0 z-10 bg-white">{totals.picked}</td>
                              </tr>
                            );
                          })}
                          {/* Site total */}
                          <tr className="bg-purple-600 text-white font-semibold">
                            <td className="p-2 sticky left-0 z-20" style={{backgroundColor: '#9333ea'}}>Total {siteData.site_name}</td>
                            {viewMode !== 'day' && datesInRange.map(d => {
                              // Filter out rejected missions
                              const daySchedules = siteData.schedules.filter((s) => s.date === d && isOperationalSchedule(s));
                              const dayTotals = { delivered: 0, returned: 0, picked: 0, nonCollected: 0 };
                              daySchedules.forEach(s => {
                                const stats = getDeliveryStats(s);
                                dayTotals.delivered += stats.delivered;
                                dayTotals.returned += stats.returned;
                                dayTotals.picked += stats.picked;
                                dayTotals.nonCollected += stats.nonCollected;
                              });
                              const hasData = dayTotals.delivered > 0 || dayTotals.returned > 0 || dayTotals.picked > 0 || dayTotals.nonCollected > 0;
                              return (
                                <td key={d} className="p-2 text-center text-xs">
                                  {hasData ? (
                                    <div className="space-y-0.5">
                                      <div>{dayTotals.delivered}</div>
                                      <div>{dayTotals.returned}</div>
                                      <div>{dayTotals.picked}</div>
                                    </div>
                                  ) : '-'}
                                </td>
                              );
                            })}
                            <td className="p-2 text-center text-xl sticky right-[216px] z-20" style={{backgroundColor: '#9333ea'}}>{siteTotals.delivered}</td>
                            <td className="p-2 text-center text-xl sticky right-[144px] z-20" style={{backgroundColor: '#9333ea'}}>{siteTotals.returned}</td>
                            <td className="p-2 text-center text-xl sticky right-[72px] z-20" style={{backgroundColor: '#9333ea'}}>{siteTotals.nonCollected}</td>
                            <td className="p-2 text-center text-xl sticky right-0 z-20" style={{backgroundColor: '#9333ea'}}>{siteTotals.picked}</td>
                          </tr>
                        </tbody>
                      </table>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })
        )}

        {/* Daily Detail View - Shows all missions for selected day with full details */}
        {viewMode === 'day' && (
          <Card>
            <CardHeader className="py-3 px-4 bg-slate-100">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Détails journaliers - {new Date(currentDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-semibold">Livreur</th>
                    <th className="text-left p-3 font-semibold">Site</th>
                    <th className="text-center p-3 font-semibold">H. Début</th>
                    <th className="text-center p-3 font-semibold">H. Fin</th>
                    <th className="text-center p-3 font-semibold">Plaque</th>
                    <th className="text-center p-3 font-semibold">PDA</th>
                    <th className="text-center p-3 font-semibold bg-green-100">Livrés</th>
                    <th className="text-center p-3 font-semibold bg-orange-100">Retours</th>
                    <th className="text-center p-3 font-semibold bg-red-100">Non Coll.</th>
                    <th className="text-center p-3 font-semibold bg-blue-100">Collectés</th>
                    <th className="text-center p-3 font-semibold">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules
                    .filter(s => s.date === datesInRange[0])
                    .filter(s => shouldDisplayUser(s.user_id, schedules.filter((x) => x.user_id === s.user_id)))
                    .sort((a, b) => (a.user_name || '').localeCompare(b.user_name || ''))
                    .map(schedule => {
                      const stats = getDeliveryStats(schedule);
                      const pda = pdas.find(p => p.pda_id === schedule.pda_id);
                      return (
                        <tr key={schedule.schedule_id} className="border-b hover:bg-slate-50">
                          <td className="p-3 font-medium">{schedule.user_name || 'Inconnu'}</td>
                          <td className="p-3 text-slate-600">{schedule.site_name || '-'}</td>
                          <td className="p-3 text-center font-mono font-semibold text-emerald-600">{schedule.start_time || '-'}</td>
                          <td className="p-3 text-center font-mono font-semibold text-rose-600">{schedule.end_time || '-'}</td>
                          <td className="p-3 text-center font-mono">{schedule.vehicle_plate || '-'}</td>
                          <td className="p-3 text-center font-mono font-semibold text-purple-600">
                            {pda?.numero || schedule.pda_id || '-'}
                          </td>
                          <td className="p-3 text-center font-bold text-green-700">{stats.delivered}</td>
                          <td className="p-3 text-center font-bold text-orange-700">{stats.returned}</td>
                          <td className="p-3 text-center font-bold text-red-700">{stats.nonCollected}</td>
                          <td className="p-3 text-center font-bold text-blue-700">{stats.picked}</td>
                          <td className="p-3 text-center">
                            <span className={`inline-block px-2 py-1 rounded-full text-[10px] sm:text-xs leading-tight whitespace-normal break-words max-w-[110px] sm:max-w-none ${
                              isValidationApproved(schedule) ? 'bg-green-100 text-green-700' :
                              getValidationStatus(schedule) === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {isValidationApproved(schedule) ? 'Validé' :
                               getValidationStatus(schedule) === 'rejected' ? 'Rejeté' : pendingValidationLabel}
                            </span>
                            {isValidationApproved(schedule) && schedule.validated_at && (
                              <div className="mt-1 text-[10px] text-slate-500" data-testid={`cp-rj-validated-at-${schedule.schedule_id}`}>
                                {formatValidatedAtParis(schedule.validated_at)} (Paris)
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  {schedules
                    .filter(s => s.date === datesInRange[0])
                    .filter(s => shouldDisplayUser(s.user_id, schedules.filter((x) => x.user_id === s.user_id)))
                    .length === 0 && (
                    <tr>
                      <td colSpan={11} className="p-8 text-center text-slate-500">
                        Aucune mission pour cette date
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Legend */}
        <Card>
          <CardContent className="p-3 space-y-3 text-sm">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-1"><div className="w-3 h-3 bg-green-500 rounded"></div><span>Livrés</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 bg-orange-500 rounded"></div><span>Retours</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded"></div><span>Non collectés</span></div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-500 rounded"></div><span>Collectes</span></div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t" data-testid="cp-rj-status-legend">
              <span className="text-xs font-semibold text-gray-600">Statut RJ :</span>
              {[
                { key: 'planifie', label: 'Planifié' },
                { key: 'travaille', label: 'Travaillé' },
                { key: 'non_travaille', label: 'Non travaillé' },
                { key: 'non_valide', label: pendingValidationLabel },
                { key: 'repos', label: 'Repos' },
                { key: 'absence', label: 'Absence' },
                { key: 'annule', label: 'Annulé' }
              ].map((item) => (
                <span key={item.key} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold ${getSimpleStatusClass(item.key)}`}>
                  {item.label}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Hours Management Section - Always visible */}
        <Card data-testid="hours-management-cp">
          <CardHeader className="py-3 px-4 bg-amber-50 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Gestion des Heures
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigateHoursDate(-1)} data-testid="hours-prev-day-cp">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-semibold min-w-[180px] text-center" data-testid="hours-current-date-cp">
                  {hoursDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <Button variant="outline" size="sm" onClick={() => navigateHoursDate(1)} data-testid="hours-next-day-cp">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {Object.keys(editedHours).length > 0 && (
                  <span className="text-xs text-amber-600 font-medium">{Object.keys(editedHours).length} modification(s)</span>
                )}
                <Button 
                  size="sm" 
                  onClick={saveAllHours} 
                  disabled={savingHours || Object.keys(editedHours).length === 0}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="hours-save-btn-cp"
                >
                  <Check className="w-4 h-4 mr-1" />
                  {savingHours ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-amber-50/50">
                  <th className="text-left p-3 font-semibold">Chauffeur</th>
                  <th className="text-left p-3 font-semibold">Site</th>
                  <th className="text-center p-3 font-semibold">H. Début</th>
                  <th className="text-center p-3 font-semibold">H. Fin</th>
                  <th className="text-center p-3 font-semibold bg-amber-100">Total</th>
                  <th className="text-center p-3 font-semibold">Plaque</th>
                  <th className="text-center p-3 font-semibold">Statut</th>
                </tr>
              </thead>
              <tbody>
                {hoursSchedules
                  .sort((a, b) => (a.user_name || '').localeCompare(b.user_name || ''))
                  .map(schedule => {
                    const edited = editedHours[schedule.schedule_id] || {};
                    const currentStart = edited.start_time !== undefined ? edited.start_time : (schedule.start_time || '');
                    const currentEnd = edited.end_time !== undefined ? edited.end_time : (schedule.end_time || '');
                    const isModified = edited.start_time !== undefined || edited.end_time !== undefined;
                    const total = isValidationApproved(schedule) ? calcTotalHours(currentStart, currentEnd) : '-';
                    return (
                      <tr key={schedule.schedule_id} className={`border-b hover:bg-amber-50/30 ${isModified ? 'bg-amber-50/50' : ''}`}>
                        <td className="p-3 font-medium">{schedule.user_name || 'Inconnu'}</td>
                        <td className="p-3 text-slate-600 text-xs">{schedule.site_name || '-'}</td>
                        <td className="p-2 text-center">
                          <input
                            type="time"
                            value={currentStart}
                            onChange={(e) => handleHourChange(schedule.schedule_id, 'start_time', e.target.value)}
                            className="border rounded px-2 py-1 text-center font-mono text-sm w-24 focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                            data-testid={`hours-start-${schedule.schedule_id}`}
                          />
                        </td>
                        <td className="p-2 text-center">
                          <input
                            type="time"
                            value={currentEnd}
                            onChange={(e) => handleHourChange(schedule.schedule_id, 'end_time', e.target.value)}
                            className="border rounded px-2 py-1 text-center font-mono text-sm w-24 focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                            data-testid={`hours-end-${schedule.schedule_id}`}
                          />
                        </td>
                        <td className="p-3 text-center font-bold font-mono text-amber-700" data-testid={`hours-total-${schedule.schedule_id}`}>{total}</td>
                        <td className="p-3 text-center font-mono text-xs">{schedule.vehicle_plate || '-'}</td>
                        <td className="p-3 text-center">
                          <span className={`inline-block px-2 py-1 rounded-full text-[10px] sm:text-xs leading-tight whitespace-normal break-words max-w-[110px] sm:max-w-none ${
                            isValidationApproved(schedule) ? 'bg-green-100 text-green-700' :
                            getValidationStatus(schedule) === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {isValidationApproved(schedule) ? 'Validé' :
                             getValidationStatus(schedule) === 'rejected' ? 'Rejeté' : pendingValidationLabel}
                          </span>
                          {isValidationApproved(schedule) && schedule.validated_at && (
                            <div className="mt-1 text-[10px] text-slate-500" data-testid={`cp-hours-validated-at-${schedule.schedule_id}`}>
                              {formatValidatedAtParis(schedule.validated_at)} (Paris)
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                {hoursSchedules.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-500">
                      Aucune mission pour cette date
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Edit Delivery Stats Modal */}
      {editModal.open && editModal.schedule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="p-4 border-b bg-purple-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Edit2 className={`w-5 h-5 ${editModal.isNew ? 'text-green-600' : 'text-purple-600'}`} />
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">
                      {editModal.isNew ? 'Ajouter une mission' : 'Modifier la prestation'}
                    </h3>
                    <p className="text-sm text-gray-600">📦 Colis Privé</p>
                  </div>
                </div>
                <button onClick={closeEditModal} className="p-1 hover:bg-gray-200 rounded-full">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Info */}
            <div className={`p-4 border-b ${editModal.isNew ? 'bg-green-50' : 'bg-gray-50'}`}>
              <div className="space-y-1 text-sm">
                <p><strong>Employé:</strong> {editModal.schedule.user_name || 'Inconnu'}</p>
                <p><strong>Date:</strong> {new Date(editModal.schedule.date).toLocaleDateString('fr-FR', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                })}</p>
                {editModal.isNew && (
                  <p className="text-green-700 font-medium mt-2">⚠️ L&apos;employé n&apos;a pas validé sa mission ce jour-là</p>
                )}
              </div>
            </div>

            {/* Form */}
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-1">Livrés</label>
                  <input
                    type="number"
                    min="0"
                    value={editModal.delivered}
                    onChange={(e) => setEditModal(prev => ({ ...prev, delivered: parseInt(e.target.value) || 0 }))}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg text-center"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-orange-700 mb-1">Retours</label>
                  <input
                    type="number"
                    min="0"
                    value={editModal.returned}
                    onChange={(e) => setEditModal(prev => ({ ...prev, returned: parseInt(e.target.value) || 0 }))}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg text-center"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">Collectés</label>
                  <input
                    type="number"
                    min="0"
                    value={editModal.picked}
                    onChange={(e) => setEditModal(prev => ({ ...prev, picked: parseInt(e.target.value) || 0 }))}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg text-center"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plaque du véhicule
                </label>
                <input
                  type="text"
                  value={editModal.vehiclePlate}
                  onChange={(e) => setEditModal(prev => ({ ...prev, vehiclePlate: e.target.value }))}
                  placeholder="Ex: AB-123-CD"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PDA utilisé
                </label>
                <select
                  value={editModal.pdaId}
                  onChange={(e) => setEditModal(prev => ({ ...prev, pdaId: e.target.value }))}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg bg-white"
                >
                  <option value="">Sélectionner un PDA</option>
                  {pdas.map(pda => (
                    <option key={pda.pda_id} value={pda.pda_id}>{pda.numero}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-t bg-gray-50 space-y-3">
              {/* Delete button - only for existing missions */}
              {!editModal.isNew && (
                <button
                  onClick={deleteMission}
                  disabled={editModal.deleting || editModal.saving}
                  className="w-full px-4 py-2 bg-red-100 text-red-700 border border-red-300 rounded-lg hover:bg-red-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editModal.deleting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {editModal.deleting ? 'Suppression...' : 'Supprimer cette mission'}
                </button>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={closeEditModal}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={saveDeliveryStats}
                  disabled={editModal.saving || editModal.deleting}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editModal.saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  {editModal.saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default AdminStatsColisPrive;
